import {Button} from '@wandb/weave/components';
import React, {useState} from 'react';

import * as GroupSelectionsTypes from '../../../state/views/groupSelections/types';
import {GroupSelectionState} from '../../../util/selectionmanager';
import {InstrumentedCenteredWaveLoader} from '../../utility/InstrumentedLoader';
import {MoveRunDrawer} from './MoveRunDrawer';
import {Body, Container, Drawer, Header} from './MoveRunDrawerPieces';
import {useTrackRunsWithLocation} from './moveRunsEvents';
import {useSelectedRuns} from './useSelectedRuns';

type DrawerLoadingProps = {
  isInReport: boolean;
  open: boolean;
  onClose: () => void;
};

/**
 * This component is rendered while we convert from the groupTempSelection to
 * the concrete list of runs we are about to move. It is intended to look like
 * the `MoveRunDrawer` so that going from loading->done isn't a jarring
 * transition.
 *
 * Ideally this component goes away when we refactor RunSelector, such that we
 * can more-easily track which runs are being selected in the client, making the
 * additional gql request redundant.
 */
function DrawerLoading({isInReport, open, onClose}: DrawerLoadingProps) {
  return (
    <Drawer open={open} onClose={onClose}>
      <Container isInReport={isInReport}>
        <Header onClose={onClose} title="Move runs to another group" />
        <Body>
          <InstrumentedCenteredWaveLoader name="load-all-groups" size="small" />
        </Body>
      </Container>
    </Drawer>
  );
}

type MoveRunDrawerBulkProps = {
  entityName: string;
  groupTempSelection: GroupSelectionState;
  isInReport: boolean;
  onClose: () => void;
  onMovedRuns: () => void;
  open: boolean;
  projectName: string;
  groupSelectionsRef: GroupSelectionsTypes.Ref;
};

function MoveRunDrawerBulk({
  entityName,
  groupTempSelection,
  isInReport,
  onClose,
  onMovedRuns,
  open,
  projectName,
  groupSelectionsRef,
}: MoveRunDrawerBulkProps) {
  const {runs, loading} = useSelectedRuns({
    entityName,
    projectName,
    groupTempSelection,
    skip: !open,
  });

  if (loading) {
    return (
      <DrawerLoading isInReport={isInReport} open={open} onClose={onClose} />
    );
  }
  return (
    <MoveRunDrawer
      entityName={entityName}
      isInReport={isInReport}
      onClose={onClose}
      onMovedRuns={onMovedRuns}
      open={open}
      projectName={projectName}
      runs={runs}
      groupSelectionsRef={groupSelectionsRef}
    />
  );
}

type MoveRunDrawerBulkButton = {
  entityName: string;
  groupTempSelection: GroupSelectionState;
  isInReport: boolean;
  onMovedRuns: () => void;
  projectName: string;
  groupSelectionsRef: GroupSelectionsTypes.Ref;
};
export function MoveRunDrawerBulkButton({
  entityName,
  groupTempSelection,
  isInReport,
  onMovedRuns,
  projectName,
  groupSelectionsRef,
}: MoveRunDrawerBulkButton) {
  const [moveRunDrawerOpen, setMoveRunDrawerOpen] = useState(false);
  const trackMoveRunsWithLocation = useTrackRunsWithLocation();

  return (
    <>
      <MoveRunDrawerBulk
        entityName={entityName}
        groupTempSelection={groupTempSelection}
        isInReport={isInReport}
        onClose={() => setMoveRunDrawerOpen(false)}
        onMovedRuns={() => {
          setMoveRunDrawerOpen(false);
          onMovedRuns();
          trackMoveRunsWithLocation('Moved runs');
        }}
        open={moveRunDrawerOpen}
        projectName={projectName}
        groupSelectionsRef={groupSelectionsRef}
      />
      <Button
        variant="secondary"
        icon="forward-next"
        aria-label="Move to group"
        onClick={() => setMoveRunDrawerOpen(true)}>
        Move to group
      </Button>
    </>
  );
}
